import { useEffect, useState } from "react";
import { Loader2, Mail, Clock, Users, ShoppingBag } from "lucide-react";

function Stat({ label, value, testId }) {
  return (
    <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-4" data-testid={testId}>
      <div className="kicker text-[#6E6E6E]">{label}</div>
      <div className="font-head text-2xl text-[#1E1E1E] mt-1">{value}</div>
    </div>
  );
}

function JourneyRow({ name, data }) {
  return (
    <div className="flex items-center justify-between border-b border-[#D3CFC0] py-2 text-sm" data-testid={`journey-row-${name}`}>
      <span className="font-head uppercase tracking-wide text-[#1E1E1E]">{name.replace(/_/g, " ")}</span>
      <span className="text-[#6E6E6E]">
        <b className="text-[#1E1E1E]">{data.active}</b> active · {data.completed} done · {data.suppressed} suppressed · {data.total} total
      </span>
    </div>
  );
}

export default function JourneysAdmin({ api }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => {
    setErr(false);
    api.get("/api/admin/journeys").then(({ data }) => setData(data)).catch(() => setErr(true));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (err) return <div className="mt-8 text-[#B4452F]" data-testid="journeys-error">Couldn't load journeys.</div>;
  if (!data) return <div className="mt-8 flex items-center gap-2 text-[#6E6E6E]"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

  const ns = data.next_scheduled_newsletter;
  const ne = data.next_scheduled_email;

  return (
    <div className="mt-8 space-y-6" data-testid="admin-journeys">
      <div className={`border-2 p-4 ${data.newsletter_send_enabled ? "border-[#2E7D57] bg-[#E7F0EA]" : "border-[#DB9130] bg-[#F7EEDD]"}`} data-testid="journeys-send-status">
        <div className="flex items-center gap-2 font-head uppercase tracking-wide text-sm text-[#1E1E1E]">
          <Mail size={16} />
          Newsletter sending: {data.newsletter_send_enabled ? "ENABLED (production)" : "SIMULATION (off)"}
        </div>
        <p className="text-xs text-[#6E6E6E] mt-1">{data.note}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Active subscribers" value={data.subscribers.active_subscribers} testId="stat-subscribers" />
        <Stat label="Unsubscribed" value={data.subscribers.unsubscribed_accounts} testId="stat-unsub" />
        <Stat label="Drafts" value={data.newsletter_issue_status_counts.draft} testId="stat-drafts" />
        <Stat label="Sent issues" value={data.newsletter_issue_status_counts.sent} testId="stat-sent" />
      </div>

      <div className="border-2 border-[#D3CFC0] bg-[#F3F1EC] p-4" data-testid="journeys-scheduled">
        <div className="flex items-center gap-2 kicker text-[#6E6E6E]"><Clock size={14} /> Next scheduled newsletter</div>
        {ns ? (
          <p className="text-sm text-[#1E1E1E] mt-1">#{ns.issue_number} — “{ns.subject}” · {new Date(ns.scheduled_for).toLocaleString()}</p>
        ) : (
          <p className="text-sm text-[#6E6E6E] mt-1">None scheduled.</p>
        )}
      </div>

      <div className="border-2 border-[#D3CFC0] bg-[#F3F1EC] p-4">
        <div className="flex items-center gap-2 kicker text-[#6E6E6E] mb-2"><Users size={14} /> Email journeys</div>
        <JourneyRow name="general_welcome" data={data.journeys.general_welcome} />
        <JourneyRow name="coach_nurture" data={data.journeys.coach_nurture} />
        <JourneyRow name="parent_nurture" data={data.journeys.parent_nurture} />
        <JourneyRow name="post_purchase" data={data.journeys.post_purchase} />
      </div>

      <div className="border-2 border-[#D3CFC0] bg-[#F3F1EC] p-4" data-testid="journeys-next-email">
        <div className="flex items-center gap-2 kicker text-[#6E6E6E]"><ShoppingBag size={14} /> Next scheduled automated email (live recipients)</div>
        {ne ? (
          <p className="text-sm text-[#1E1E1E] mt-1">
            [{ne.track}] “{ne.subject}” · {new Date(ne.send_at).toLocaleString()} {ne.due ? "· DUE now" : ""}
          </p>
        ) : (
          <p className="text-sm text-[#6E6E6E] mt-1">No pending automated emails for live recipients.</p>
        )}
      </div>

      <button onClick={load} data-testid="journeys-refresh"
        className="font-head uppercase tracking-wide text-xs border-2 border-[#2D2D2D] text-[#2D2D2D] px-4 py-2 hover:bg-[#2D2D2D] hover:text-[#F3F1EC] transition-colors">
        Refresh
      </button>
    </div>
  );
}
